import { useMemo } from 'react';
import { validatePasswordStrength } from '@/utils/password-validation';
import { cn } from '@/lib/utils';

interface PasswordStrengthMeterProps {
  password: string;
  className?: string;
}

type StrengthLevel = 'very-weak' | 'weak' | 'fair' | 'good' | 'excellent';

interface StrengthDisplay {
  level: StrengthLevel;
  percentage: number;
  label: string;
  feedback: string;
  colorClass: string;
  textColorClass: string;
}

/**
 * Maps password validation strength to visual display properties
 * @param strength - Strength level from password validation ('weak' | 'fair' | 'good' | 'strong')
 * @param requirementsMet - Number of requirements met (0-6)
 * @returns Visual properties for strength display
 */
function getStrengthDisplay(strength: string, requirementsMet: number): StrengthDisplay {
  // Map validation strength + requirements to 5 levels
  if (requirementsMet <= 1) {
    return {
      level: 'very-weak',
      percentage: 20,
      label: 'Very Weak',
      feedback: 'Add more character variety and length',
      colorClass: 'bg-strength-very-weak',
      textColorClass: 'text-strength-very-weak-foreground'
    };
  }
  
  if (requirementsMet === 2 || strength === 'weak') {
    return {
      level: 'weak',
      percentage: 40,
      label: 'Weak',
      feedback: 'Try adding numbers and special characters',
      colorClass: 'bg-strength-weak',
      textColorClass: 'text-strength-weak-foreground'
    };
  }
  
  if (requirementsMet <= 4 || strength === 'fair') {
    return {
      level: 'fair',
      percentage: 60,
      label: 'Fair',
      feedback: 'Getting better! Add more variety',
      colorClass: 'bg-strength-fair',
      textColorClass: 'text-strength-fair-foreground'
    };
  }
  
  if (strength === 'good') {
    return {
      level: 'good',
      percentage: 80,
      label: 'Good',
      feedback: 'Strong password! Consider making it longer',
      colorClass: 'bg-strength-good',
      textColorClass: 'text-strength-good-foreground'
    };
  }
  
  // strength === 'strong' and all requirements met
  return {
    level: 'excellent',
    percentage: 100,
    label: 'Excellent',
    feedback: 'Excellent! Your password is very strong',
    colorClass: 'bg-strength-excellent',
    textColorClass: 'text-strength-excellent-foreground'
  };
}

/**
 * Real-time password strength meter component
 * Displays visual feedback with progress bar and contextual messages
 * WCAG 2.1 AA compliant with screen reader support
 */
export const PasswordStrengthMeter = ({ password, className }: PasswordStrengthMeterProps) => {
  const strengthDisplay = useMemo(() => {
    if (!password || password.length === 0) {
      return null;
    }

    const validation = validatePasswordStrength(password);
    const requirementsMet = Object.values(validation.requirements).filter(Boolean).length;
    
    return getStrengthDisplay(validation.strength, requirementsMet);
  }, [password]);

  if (!strengthDisplay) {
    return null;
  }

  return (
    <div 
      className={cn(
        "space-y-2 animate-fade-in",
        className
      )}
      role="region"
      aria-label="Password strength indicator"
    >
      {/* Progress Bar */}
      <div className="relative h-1 w-full bg-muted rounded-full overflow-hidden">
        <div
          className={cn(
            "h-full transition-all duration-200 ease-out rounded-full",
            strengthDisplay.colorClass
          )}
          style={{ width: `${strengthDisplay.percentage}%` }}
          role="progressbar"
          aria-valuenow={strengthDisplay.percentage}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Password strength indicator"
        />
      </div>

      {/* Feedback Text */}
      <div
        className={cn(
          "text-sm font-medium transition-colors duration-200",
          strengthDisplay.textColorClass
        )}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <span className="font-semibold">{strengthDisplay.label}:</span>{' '}
        <span className="font-normal">{strengthDisplay.feedback}</span>
      </div>
    </div>
  );
};
